import { useComplexQuery, UseQueryResult } from '@sequencehq/api'
import {
  Dashboardv20240509Api,
  dashboardv20240509Client
} from '@sequencehq/api/dashboard/v20240509'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { parse } from '@sequencehq/utils/dates'
import { API_DATE_FORMAT } from 'BillingScheduleHome/billingScheduleHome.constants'
import uniq from 'lodash/fp/uniq'

export type BillingScheduleAggregatePrice = Exclude<
  DashboardApi20240730.GetPrice.Response['data'],
  null
>
type ApiProduct = Exclude<
  Dashboardv20240509Api.GetProduct.Response['data'],
  null
>

export type BillingSchedulePhaseWithParsedDates = Omit<
  DashboardApi20240730.GetBillingSchedule.BillingSchedulePhase,
  'startDate' | 'endDate'
> & {
  startDate: Date
  endDate: Date | undefined
}

export const BILLING_SCHEDULE_AGGREGATE_BASE_QUERY_KEY =
  'billingScheduleAggregate'

export const fetchBillingScheduleAggregate = async (
  billingScheduleId: string
) => {
  const [billingScheduleResponse, billingScheduleSettingsResponse] =
    await Promise.all([
      dashboard20240730Client.getBillingSchedule({
        id: billingScheduleId
      }),
      dashboard20240730Client.getBillingScheduleSettings({
        billingScheduleId
      })
    ])

  if (billingScheduleResponse.error || !billingScheduleResponse.data) {
    return Promise.reject({
      statusText: billingScheduleResponse.res.statusText,
      status: billingScheduleResponse.res.status
    })
  }

  const billingSchedule = billingScheduleResponse.data

  const allPriceIds = uniq(
    billingScheduleResponse.data.phases?.flatMap(({ priceIds }) => priceIds)
  )
  const taxRateId = billingSchedule.taxRates[0]?.taxRateId
  const customerId = billingSchedule.customerId

  const [
    taxRateResponse,
    allPriceResponses,
    customerContactsResponse,
    customerResponse
  ] = await Promise.all([
    taxRateId
      ? dashboardv20240509Client.getTaxRate({ id: taxRateId })
      : Promise.resolve(null),
    Promise.all(
      allPriceIds.map(priceId =>
        dashboard20240730Client.getPrice({
          id: priceId
        })
      )
    ),
    dashboardv20240509Client.getCustomerContacts({
      customerId
    }),
    dashboard20240730Client.getCustomer({
      id: customerId
    })
  ])

  const prices = allPriceResponses
    .map(res => res.data)
    .filter((price): price is BillingScheduleAggregatePrice => Boolean(price))

  const productIds = prices.map(price => price.productId)

  const allProductResponses = await Promise.all(
    productIds.map(productId =>
      dashboardv20240509Client.getProduct({
        id: productId
      })
    )
  )

  const products = allProductResponses
    .map(res => res.data)
    .filter((product): product is ApiProduct => Boolean(product))

  const customerContacts = customerContactsResponse.data?.items ?? []

  const customer = customerResponse.data

  const scheduleStartDate = parse(
    billingSchedule.startDate,
    API_DATE_FORMAT,
    new Date()
  )
  const scheduleEndDate = billingSchedule.endDate
    ? parse(billingSchedule.endDate, API_DATE_FORMAT, new Date())
    : undefined
  const phasesWithParsedDates = billingSchedule.phases.map(phase => ({
    ...phase,
    startDate: parse(phase.startDate, API_DATE_FORMAT, new Date()),
    endDate: phase.endDate
      ? parse(phase.endDate, API_DATE_FORMAT, new Date())
      : undefined
  }))

  return {
    billingSchedule: {
      ...billingSchedule,
      startDate: scheduleStartDate,
      endDate: scheduleEndDate,
      phases: phasesWithParsedDates
    },
    billingScheduleSettings: billingScheduleSettingsResponse.data,
    prices,
    products,
    taxRate: taxRateResponse ? taxRateResponse.data : null,
    customer,
    customerContacts
  }
}

export const getBillingScheduleAggregateKey = (billingScheduleId: string) => [
  /**
   * This key ensures that anything that would invalidate a dependency
   * also invalidates the aggregate. This is because our invalidation
   * will invalidate a cache key as long as the key is contained within
   * the overall query key.
   *
   * Note that this pattern, and the change to invalidation made in the same
   * PR, lays the foundations for defining aggregate dependencies within
   * our API package. When we introduce BE aggregates, that will be incredibly
   * useful! Of course, the full version of that will be cleaner than this,
   * but the concept is similar.
   */
  BILLING_SCHEDULE_AGGREGATE_BASE_QUERY_KEY,
  ...dashboard20240730Client.getBillingSchedule.queryKey,
  ...dashboard20240730Client.getBillingScheduleSettings.queryKey,
  ...dashboard20240730Client.getPrice.queryKey,
  ...dashboardv20240509Client.getProduct.queryKey,
  ...dashboardv20240509Client.getTaxRate.queryKey,
  { billingScheduleId }
]

export type BillingScheduleAggregateData = Awaited<
  ReturnType<typeof fetchBillingScheduleAggregate>
>
export const useBillingScheduleAggregate = <
  S extends (data: BillingScheduleAggregateData) => unknown
>(props: {
  billingScheduleId: string
  select: S
}): UseQueryResult<ReturnType<S>, { statusText: string; status: number }> =>
  useComplexQuery({
    queryFn: () => fetchBillingScheduleAggregate(props.billingScheduleId),
    queryKey: getBillingScheduleAggregateKey(props.billingScheduleId),
    staleTime: 60000,
    select: props.select
  }) as UseQueryResult<ReturnType<S>, { statusText: string; status: number }>
